import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import { langCodeToJapanese } from "@/lib/chillnn/lang-code";
import type { LangCode, RoomI18n_DesignPageQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { ToastAction } from "@/components/ui/toast/toast";

import * as Shared from "../../../../../_shared";
import { useRoomI18nArticleUpdateMutation } from "../../repository/mutation/use-room-i18n-article-update-mutation";

type Props = {
	hotelID: string;
	roomID: string;
	langCode: LangCode;
	data: RoomI18n_DesignPageQuery;
};

/**
 * @todo キャッシュされているので許容できるが、２回取得の処理が走っているので対応する
 */
export const useInitialized = ({ roomID, hotelID, langCode, data }: Props) => {
	const { article } = data.roomI18n;
	const { onUpdate: _onUpdate } = useRoomI18nArticleUpdateMutation({ hotelID, roomID, langCode });

	const navigate = useNavigate();
	const roomListPath = generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.ROOM, { hotelID });

	const onNavigateToRoomList = useCallback(() => {
		navigate(roomListPath);
	}, [roomListPath, navigate]);

	const onUpdateSuccessCallback = useCallback(() => {
		const langCodeText = langCodeToJapanese(langCode);

		notifyUserEventInfo({
			title: `部屋記事（${langCodeText}）を保存しました`,
			action: (
				<ToastAction onClick={onNavigateToRoomList} altText="一覧に戻る">
					一覧に戻る
				</ToastAction>
			),
		});
	}, [langCode, onNavigateToRoomList]);

	const { articleHTML, onSetArticleHTML, isDirty, isValid, onUpdate } =
		Shared.Model.UseCase.useCreateMagazineEditorForI18nModel({
			article,
			onUpdate: _onUpdate,
			onUpdateSuccessCallback,
		});

	return {
		data,
		articleHTML,
		onSetArticleHTML,
		isDirty,
		isValid,
		onUpdate,
	};
};
